import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { addFamilyMember, updateFamilyMember, deleteFamilyMember, restoreFamilyMember, resolveAssetUrl } from '../../services/apiService';
import { i18n } from '../../i18n/i18n';
import { showToast } from '../kid/Toast';
import { starsText } from '../kid/KidTaskCard';
import ChildNotificationControls from '../parent/ChildNotificationControls';
import { FEATURES } from '../../constants/features';
import { MemberCreationData } from './adminHelpers';
import ConfirmDialog from '../kid/ConfirmDialog';
import { PictureTile } from '../kid/KidUI';
import { useLanguage } from '../../context/LanguageContext';
import { avatarOrDefault } from '../../utils/avatars';
import { formatDeletionDate, permanentDateFromNow } from '../../utils/accountDeletion';

/** Admin → Members: members, add/edit member, children's notifications (split out of AdminPanel.tsx, 2026-10-12). */
const MembersTab: React.FC = () => {
  const { current_user, current_family, refreshFamilyData, refreshDeviceSession } = useAppContext() as any;
  const { current_language } = useLanguage();
  // Member being deleted (confirm dialog). Deleting only marks them; restorable for 30 days.
  const [memberToDelete, setMemberToDelete] = useState<any | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [editMemberModal, setEditMemberModal] = useState<{ isOpen: boolean; member: any | null; saving?: boolean; error?: string }>({
    isOpen: false,
    member: null
  });
  const [tempEditMemberData, setTempEditMemberData] = useState<{ name: string; pin: string; is_admin: boolean; member_type: 'adult' | 'child' }>({
    name: '',
    pin: '',
    is_admin: false,
    member_type: 'child'
  });
  const [tempMemberData, setTempMemberData] = useState<MemberCreationData>({
    name: '',
    username: '',
    pin: '',
    email: '',
    isAdmin: false,
    member_type: 'child'
  });

  const handleCreateMember = async () => {
    try {
      if (!tempMemberData.name?.trim() || !tempMemberData.username?.trim() || !tempMemberData.pin?.trim()) {
        showToast({ message: i18n.t('admin_panel.err_member_required'), tone: 'error' });
        return;
      }
      if (!/^\d{4,8}$/.test(tempMemberData.pin.trim())) {
        showToast({ message: i18n.t('admin_panel.err_pin_format'), tone: 'error' });
        return;
      }
      if (tempMemberData.username.trim().length < 2) {
        showToast({ message: i18n.t('admin_panel.err_username_short'), tone: 'error' });
        return;
      }

      await addFamilyMember(
        tempMemberData.name.trim(),
        tempMemberData.username.trim(),
        tempMemberData.pin.trim(),
        tempMemberData.email?.trim() || undefined,
        tempMemberData.isAdmin || false,
        tempMemberData.member_type
      );

      // Refresh family data and device session to show the new member
      await refreshFamilyData();
      await refreshDeviceSession?.();
      
      setShowAddMemberModal(false);
      setTempMemberData({
        name: '',
        username: '',
        pin: '',
        email: '',
        isAdmin: false,
        member_type: 'child'
      });
    } catch (error) {
      console.error('Failed to create member:', error);
      showToast({ message: i18n.t('admin_panel.err_member_create'), tone: 'error' });
    }
  };

  if (!current_family || !current_user) return null;

  return (
    <>
    <div className="mb-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.member_management')}</h3>
        <button 
          className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
          onClick={() => setShowAddMemberModal(true)}
        >
          + {i18n.t('admin_panel.add_new_member')}
        </button>
      </div>
      
      <div className="space-y-4">
        {current_family.members.map((member: any) => (
          <div key={member.id} className="bg-kid-card dark:bg-kid-card-dark rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark p-4">
            <div className="flex justify-between items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-kid-text dark:text-kid-text-dark">{member.name}</span>
                {(member as any).is_admin && <span className="text-yellow-500 text-lg">👑</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-kid-text dark:text-kid-text-dark">
                  {i18n.t('admin_panel.member_type_label')}: {member.member_type === 'adult' ? i18n.t('admin_panel.member_type_adult') : i18n.t('admin_panel.member_type_child')}
                </span>
                <button
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                  onClick={() => {
                    setEditMemberModal({ isOpen: true, member });
                    setTempEditMemberData({ name: member.name || '', pin: '', is_admin: !!(member as any).is_admin, member_type: member.member_type });
                  }}
                >
                  {i18n.t('admin_panel.edit')}
                </button>
              </div>
            </div>
            <div className="text-kid-text dark:text-kid-text-dark text-sm mt-2">
              <span className="inline-flex items-center gap-1 font-bold">⭐ {starsText(Number(member.points) || 0)}</span>
              {FEATURES.coins && <>, Coins: {(member as any).coins ?? member.stars ?? 0}</>}
            </div>
          </div>
        ))}
      </div>
      <ChildNotificationControls members={current_family.members} />

      {(current_family.deleted_members || []).length > 0 && (
        <section className="mt-8">
          <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark mb-1">{i18n.t('deletion.recently_deleted')}</h3>
          <p className="text-base text-kid-text dark:text-kid-text-dark mb-4">{i18n.t('deletion.recently_deleted_hint')}</p>
          <div className="space-y-3">
            {current_family.deleted_members.map((m: any) => (
              <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-kid-card border-2 border-dashed border-kid-border-strong dark:border-kid-border-strong-dark p-4">
                <PictureTile src={resolveAssetUrl(avatarOrDefault(m.avatar_url))} label={m.name} size={44} rounded="rounded-full" />
                <span className="flex-1 min-w-[10rem]">
                  <span className="block text-lg font-bold">{m.name}</span>
                  <span className="block text-sm">{i18n.t('deletion.gone_on').replace('{{date}}', formatDeletionDate(m.permanent_at, current_language))}</span>
                </span>
                <button
                  type="button"
                  disabled={restoringId === m.id}
                  onClick={async () => {
                    setRestoringId(m.id);
                    try {
                      await restoreFamilyMember(m.id);
                      await refreshFamilyData();
                      await refreshDeviceSession?.();
                      showToast({ message: i18n.t('deletion.member_restored_toast').replace('{{name}}', m.name) });
                    } catch (e: any) {
                      showToast({ message: e?.message || 'Error', tone: 'error' });
                    } finally {
                      setRestoringId(null);
                    }
                  }}
                  className="inline-flex items-center justify-center min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
                >
                  {restoringId === m.id ? '…' : i18n.t('deletion.restore')}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
      <ConfirmDialog
        open={!!memberToDelete}
        title={memberToDelete ? i18n.t('deletion.delete_member_title').replace('{{name}}', memberToDelete.name) : ''}
        message={memberToDelete && (
          <div className="space-y-3">
            <p>{i18n.t('deletion.delete_member_text').replace('{{name}}', memberToDelete.name)}</p>
            <p className="font-bold">{i18n.t('deletion.delete_member_lost').replace('{{name}}', memberToDelete.name)}</p>
            <p>{i18n.t('deletion.undo_30').replace('{{date}}', formatDeletionDate(permanentDateFromNow(), current_language))}</p>
          </div>
        )}
        confirmLabel={i18n.t('deletion.delete_member_yes')}
        cancelLabel={i18n.t('deletion.cancel')}
        onCancel={() => setMemberToDelete(null)}
        onConfirm={async () => {
          const m = memberToDelete;
          await deleteFamilyMember(m.id);
          setMemberToDelete(null);
          setEditMemberModal({ isOpen: false, member: null });
          await refreshFamilyData();
          await refreshDeviceSession?.();
          showToast({ message: i18n.t('deletion.member_deleted_toast').replace('{{name}}', m.name) });
        }}
      />
      {/* Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10050] p-4">
          <div className="bg-kid-card dark:bg-kid-card-dark rounded-kid-tile max-w-md w-full mx-4 border-2 border-kid-border dark:border-kid-border-dark">
            <div className="mb-4">
              <h3 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.member_modal_title_add')}</h3>
            </div>
            <div className="mb-6 space-y-4">
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.member_name_label_required')}</label>
                <input 
                  type="text" 
                  placeholder={i18n.t('admin_panel.member_name_placeholder')}
                  value={tempMemberData.name || ''}
                  onChange={(e) => setTempMemberData({ ...tempMemberData, name: e.target.value })} 
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                />
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('library.username_required')}</label>
                <input 
                  type="text" 
                  placeholder={i18n.t('library.username_ph')}
                  value={tempMemberData.username || ''}
                  onChange={(e) => setTempMemberData({ ...tempMemberData, username: e.target.value })} 
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                />
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('library.pin_label')}</label>
                <input 
                  type="password" 
                  placeholder={i18n.t('library.pin_ph')}
                  value={tempMemberData.pin || ''}
                  onChange={(e) => setTempMemberData({ ...tempMemberData, pin: e.target.value })} 
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                />
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.member_email_label_optional')}</label>
                <input 
                  type="email" 
                  placeholder={i18n.t('admin_panel.member_email_placeholder')}
                  value={tempMemberData.email || ''}
                  onChange={(e) => setTempMemberData({ ...tempMemberData, email: e.target.value })} 
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                />
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.admin_access_label')}</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={tempMemberData.isAdmin || false}
                    onChange={(e) => setTempMemberData({ ...tempMemberData, isAdmin: e.target.checked })} 
                    className="w-6 h-6 accent-kid-green" 
                  />
                  <span className="text-sm font-medium text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.admin_access_label')}</span>
                </label>
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.member_type_label')}</label>
                <select
                  value={tempMemberData.member_type}
                  onChange={(e) => setTempMemberData({ ...tempMemberData, member_type: e.target.value as 'adult' | 'child' })}
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                >
                  <option value="adult">{i18n.t('admin_panel.member_type_adult')}</option>
                  <option value="child">{i18n.t('admin_panel.member_type_child')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => {
                setShowAddMemberModal(false);
                setTempMemberData({
                  name: '',
                  username: '',
                  pin: '',
                  email: '',
                  isAdmin: false,
                  member_type: 'child'
                });
              }} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1">{i18n.t('admin_panel.cancel_btn')}</button>
              <button onClick={handleCreateMember} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.add_member_btn')}</button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Member Modal */}
      {editMemberModal.isOpen && editMemberModal.member && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10050] p-4">
          <div className="bg-kid-card dark:bg-kid-card-dark rounded-kid-tile max-w-md w-full mx-4 border-2 border-kid-border dark:border-kid-border-dark p-6">
            <div className="mb-4">
              <h3 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.member_edit_title')}</h3>
            </div>
            {editMemberModal.error && (
              <div className="mb-4 p-3 rounded border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                {editMemberModal.error}
              </div>
            )}
            <div className="space-y-4 mb-6">
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.member_name_label')}</label>
                <input
                  type="text"
                  value={tempEditMemberData.name}
                  onChange={(e) => setTempEditMemberData({ ...tempEditMemberData, name: e.target.value })}
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                />
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.member_type_label')}</label>
                <select
                  value={tempEditMemberData.member_type}
                  onChange={(e) => setTempEditMemberData({ ...tempEditMemberData, member_type: e.target.value as 'adult' | 'child' })}
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                >
                  <option value="adult">{i18n.t('admin_panel.member_type_adult')}</option>
                  <option value="child">{i18n.t('admin_panel.member_type_child')}</option>
                </select>
              </div>
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.member_pin_label_optional')}</label>
                <input
                  type="password"
                  value={tempEditMemberData.pin}
                  onChange={(e) => setTempEditMemberData({ ...tempEditMemberData, pin: e.target.value })}
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                />
                <p className="text-xs text-kid-text-muted dark:text-kid-text-muted-dark mt-1">{i18n.t('admin_panel.member_pin_hint_optional')}</p>
              </div>
              <div className="w-full">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempEditMemberData.is_admin}
                    onChange={(e) => setTempEditMemberData({ ...tempEditMemberData, is_admin: e.target.checked })}
                    className="w-6 h-6 accent-kid-green"
                  />
                  <span className="text-sm font-medium text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.member_is_admin_label')}</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1"
                onClick={() => {
                  setEditMemberModal({ isOpen: false, member: null });
                  setTempEditMemberData({ name: '', pin: '', is_admin: false, member_type: 'child' });
                }}
                disabled={!!editMemberModal.saving}
              >
                {i18n.t('admin_panel.cancel_btn')}
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1"
                onClick={async () => {
                  try {
                    setEditMemberModal(prev => ({ ...prev, saving: true, error: undefined }));
                    const updates: any = {};
                    if (tempEditMemberData.name.trim() && tempEditMemberData.name.trim() !== (editMemberModal.member.name || '')) {
                      updates.name = tempEditMemberData.name.trim();
                    }
                    if (tempEditMemberData.member_type !== editMemberModal.member.member_type) {
                      updates.member_type = tempEditMemberData.member_type;
                    }
                    if (tempEditMemberData.pin.trim()) {
                      if (!/^\d{4,8}$/.test(tempEditMemberData.pin.trim())) {
                        setEditMemberModal(prev => ({ ...prev, saving: false, error: i18n.t('user_profile.pin_validation') }));
                        return;
                      }
                      updates.pin = tempEditMemberData.pin.trim();
                    }
                    if (tempEditMemberData.is_admin !== !!(editMemberModal.member as any).is_admin) {
                      updates.is_admin = tempEditMemberData.is_admin;
                    }
                    if (Object.keys(updates).length === 0) {
                      setEditMemberModal({ isOpen: false, member: null });
                      setTempEditMemberData({ name: '', pin: '', is_admin: false, member_type: 'child' });
                      return;
                    }
                    await updateFamilyMember(editMemberModal.member.id, updates);
                    await refreshFamilyData();
                    setEditMemberModal({ isOpen: false, member: null });
                    setTempEditMemberData({ name: '', pin: '', is_admin: false, member_type: 'child' });
                  } catch (error: any) {
                    const message = error?.message || i18n.t('admin_panel.member_update_failed');
                    setEditMemberModal(prev => ({ ...prev, saving: false, error: message }));
                  }
                }}
                disabled={!!editMemberModal.saving}
              >
                {i18n.t('admin_panel.save_changes_btn')}
              </button>
            </div>
            {/* Not yourself, and not the person who created the family (they go with the whole family). */}
            {editMemberModal.member.id !== current_user.id && editMemberModal.member.id !== current_family.admin_id && (
              <button
                type="button"
                onClick={() => setMemberToDelete(editMemberModal.member)}
                disabled={!!editMemberModal.saving}
                className="mt-4 w-full inline-flex items-center justify-center min-h-[48px] px-5 rounded-full font-bold text-base border-2 border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 disabled:opacity-50"
              >
                {i18n.t('deletion.delete_member')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MembersTab;
